import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { PublicUserEntity } from './public-user.entity';

@Index('fk_client__id', ['fkClientId'], {})
@Index('fk_public_user__id', ['fkPublicUserId'], {})
@Entity('client_public_user')
export class ClientPublicUserEntity extends BaseEntity {
	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('int', { name: 'fk_public_user__id', unsigned: true })
	public fkPublicUserId!: number;

	@Column({ type: 'boolean', name: 'isClientActive', width: 1, default: () => "'0'" })
	public isClientActive!: boolean;

	@Column({ type: 'boolean', name: 'isEnabled', width: 1, default: () => "'1'" })
	public isEnabled!: boolean;

	// PD-14079: when this mapping points at a parent account, the portal
	// user implicitly gains access to its child accounts too (browse,
	// order, pay on their behalf) without per-child mapping rows. Default
	// ON — this per-mapping flag is the opt-out lever for a user who
	// should only see the parent itself.
	@Column({
		type: 'boolean',
		name: 'includeChildClients',
		width: 1,
		default: () => "'1'",
	})
	public includeChildClients!: boolean;

	// PD-14187: when this mapping points at a client that belongs to an
	// ownership group, the portal user can also access the other accounts in
	// that ownership. Mirrors includeChildClients but defaults OFF — ownership
	// access is opt-in (vs the parent→children cascade which defaults on).
	@Column({
		type: 'boolean',
		name: 'includeOwnershipClients',
		width: 1,
		default: () => "'0'",
	})
	public includeOwnershipClients!: boolean;

	@Column('decimal', {
		name: 'orderApprovalLimit',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public orderApprovalLimit!: string | null;

	// PD-14239: when ON, this portal user sees NO pricing or financial
	// figures while acting as this client — or any client reached THROUGH
	// this mapping via the child (PD-14079) / ownership (PD-14187) cascade.
	// The API strips catalog / cart / order prices + totals server-side and
	// hides the invoices, transactions, statements, and payments sections
	// entirely; order history stays visible (sans money). Per user-per-client
	// and default OFF — the customer's other buyers, and this same buyer on a
	// different account, are unaffected.
	@Column({
		type: 'boolean',
		name: 'hidePricing',
		width: 1,
		default: () => "'0'",
	})
	public hidePricing!: boolean;

	@ManyToOne(() => ClientEntity, (client) => client.clientPublicUsers, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@ManyToOne(
		() => PublicUserEntity,
		(publicUser) => publicUser.clientPublicUsers,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([{ name: 'fk_public_user__id', referencedColumnName: 'id' }])
	public fkPublicUser!: PublicUserEntity;
}
